import axiosInstance from "../Axiosinstance";

/**
 * Generic API service class for making HTTP requests
 * This makes it easy to add new API endpoints without modifying multiple files
 */
class ApiService {
  /**
   * Generic GET request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Query parameters
   * @param {object} config - Additional axios config
   * @returns {Promise}
   */
  async get(endpoint, params = {}, config = {}) {
    try {
      const response = await axiosInstance.get(endpoint, {
        params,
        ...config,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic POST request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} config - Additional axios config
   * @returns {Promise}
   */
  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await axiosInstance.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} config - Additional axios config
   * @returns {Promise}
   */
  async put(endpoint, data = {}, config = {}) {
    try {
      const response = await axiosInstance.put(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic PATCH request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} config - Additional axios config
   * @returns {Promise}
   */
  async patch(endpoint, data = {}, config = {}) {
    try {
      const response = await axiosInstance.patch(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} config - Additional axios config
   * @returns {Promise}
   */
  async delete(endpoint, config = {}) {
    try {
      const response = await axiosInstance.delete(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - Axios error object
   * @returns {Error}
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const message = data?.message || data?.error || "An error occurred";
      return {
        message,
        status,
        data: data?.data || null,
        originalError: error,
      };
    } else if (error.request) {
      // Request made but no response received
      return {
        message: "No response from server. Please check your connection.",
        status: null,
        data: null,
        originalError: error,
      };
    } else {
      // Error setting up request
      return {
        message: error.message || "An unexpected error occurred",
        status: null,
        data: null,
        originalError: error,
      };
    }
  }
}

// Create and export a singleton instance
const apiService = new ApiService();

export default apiService;

